import { ComponentEntry, DealFormData, FormValue, TemplateStep } from "../../pages/deals/createDeal/dealFormTypes";
import { buildOptions, humanizeLabel, getOptionKey, getOptionLabel, normalizeComponentType } from "../../pages/deals/createDeal/dealFormFields";
import type { PayoutItem } from "../../pages/deals/createDeal/componentPaymentsStep";
import { PaymentType } from "../../types/types";
import { normalizePaymentTiming } from "./dealNormalizers";
import { TranslationFn } from "../../pages/deals/createDeal/dealFormFields";

export const buildPayoutSummary = (
  formData: DealFormData,
  optionsBySource: Record<string, Array<{ key: string; label: string }>>,
  rawTemplateSteps: TemplateStep[],
  t: TranslationFn,
) => {
  const comps = Array.isArray(formData?.components)
    ? (formData.components as ComponentEntry[])
    : ([] as ComponentEntry[]);
  const coreData = (formData?.core ?? {}) as Record<string, FormValue>;
  const effectiveBase = Number(coreData.baseAmount || 0);
  const componentsStep = rawTemplateSteps.find(
    (step) =>
      step?.stepId === "components" ||
      String(step?.title || "").toLowerCase().includes("component"),
  );
  const componentTypeField = componentsStep?.fields?.find((field) =>
    ["componentType", "type", "component"].includes(String(field?.key || "")),
  );

  const getComponentLabel = (component: Record<string, FormValue>, rawType: unknown): string => {
    const rawTypeText = String(rawType || "").trim();
    if (!rawTypeText) return "";

    if (!componentTypeField) return humanizeLabel(rawTypeText);

    const directOption = Array.isArray(componentTypeField.options)
      ? componentTypeField.options.find((option) => {
          const visibleWhen = option?.visibleWhen;
          const isVisible =
            !visibleWhen ||
            Object.entries(visibleWhen).every(
              ([key, expected]) => {
                const actual = component?.[key];
                return Array.isArray(expected)
                  ? expected.includes(actual)
                  : actual === expected;
              },
            );

          return isVisible && getOptionKey(option) === rawTypeText;
        })
      : null;
    const directLabel = directOption ? getOptionLabel(directOption) : "";
    if (directLabel) return directLabel;

    const matchingOption = buildOptions(
      componentTypeField,
      component,
      formData,
      optionsBySource,
    ).find((option) => option.key === rawTypeText);

    return matchingOption?.label || humanizeLabel(rawTypeText);
  };

  const list = comps
    .map((c: ComponentEntry, index: number) => {
      const rawType = c?.componentType ?? c?.type ?? c?.component ?? "";
      const rawVal = Number(c?.value ?? c?.amount ?? c?.price ?? 0);
      const timing = normalizePaymentTiming(
        c?.fundingStage ??
          c?.funding_stage ??
          c?.paymentTiming ??
          c?.payment_timing ??
          c?.paymentTimingKey ??
          "",
      );

      const isPercentage =
        String(c?.mode ?? "").toUpperCase() === "PERCENT_OF_BASE";

      const resolvedAmount = isPercentage
        ? (effectiveBase * rawVal) / 100.0
        : rawVal;

      return {
        original: c,
        index: index,
        componentType: normalizeComponentType(rawType),
        componentLabel: getComponentLabel(c, rawType),
        value: Number.isFinite(resolvedAmount) ? resolvedAmount : 0,
        isPercentage,
        percentageValue: isPercentage ? rawVal : undefined,
        paymentTiming: timing,
      };
    })
    .filter((c: ComponentEntry) => c.componentType || c.componentLabel || c.value !== 0);

  if (
    effectiveBase > 0 &&
    !list.some((c) => c.componentType === "BASE_AMOUNT")
  ) {
    list.unshift({
      original: {
        componentType: "BASE_AMOUNT",
        value: effectiveBase,
        paymentTiming: "PRE_MATCH",
      },
      index: -1,
      componentType: "BASE_AMOUNT",
      componentLabel: t("baseAmount"),
      value: effectiveBase,
      isPercentage: false,
      percentageValue: undefined,
      paymentTiming: "PRE_MATCH",
    });
  }

  return list;
};

// Splits the payouts into the pre-match and post-match payment groups.
// An upfront deal pays everything before the match.
export const splitPayoutsByTiming = (
  payouts: PayoutItem[],
  paymentType: PaymentType,
): { preMatchPayouts: PayoutItem[]; postMatchPayouts: PayoutItem[] } => {
  if (paymentType === PaymentType.UPFRONT) {
    return { preMatchPayouts: payouts, postMatchPayouts: [] };
  }

  return {
    preMatchPayouts: payouts.filter(
      (payout) => payout.paymentTiming !== "POST_MATCH",
    ),
    postMatchPayouts: payouts.filter(
      (payout) => payout.paymentTiming === "POST_MATCH",
    ),
  };
};
